import { Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { Badge } from '~/components/ui/badge';
import { cn } from '~/utils/cn';

import type { ListDensity } from '../store/list-view.store';
import { DENSITY } from '../utils/list-density';
import { useCrossLinkSearch } from '../utils/nav-context';
import { parseSourceProductId } from '../utils/source-product-id';

import { ProductThumb } from './product-thumb';

interface Props {
  title: string;
  imageUrl?: string | null | undefined;
  /** Full-size image for the enlarged viewer when `imageUrl` is a small thumbnail rendition. */
  fullImageUrl?: string | null | undefined;
  gallery?: (string | null | undefined)[] | undefined;
  /** Connector product id(s) of the feed rows this row stands for; parsed to offer id + markets. */
  sourceProductIds?: (string | null | undefined)[] | undefined;
  /** Extra muted identifiers for line 2 (brand, SKU, …), shown before the offer id. */
  meta?: ReactNode;
  /** Chips appended after the title line (e.g. needs-attention flag). */
  badges?: ReactNode;
  /** Design B — chips on their own wrapped row UNDER the title (type, listings, updated later),
   *  so nothing crowds the title's right edge. */
  badgesBelow?: ReactNode;
  density?: ListDensity | undefined;
  /** Wrap the title in a link / button supplied by the caller (row open). */
  renderTitle?: ((title: ReactNode) => ReactNode) | undefined;
  /**
   * Bug fix — per-market feed-product link + flagged state, keyed by the market chip text (the
   * code parsed from `sourceProductIds`, e.g. "UK", or the `marketLabels` entry). When set, each market chip links to THAT market's own
   * feed product and flagged markets render in warning tone with a small ⚠, so a grouped row's
   * chips never point at — or stay silent about — a different listing than the one flagged.
   * Additive: callers that omit it keep today's plain, unlinked chips unchanged.
   */
  marketHrefs?: Record<string, { feedProductId: string; flagged?: boolean }> | undefined;
  /**
   * Market chips to show instead of the countries parsed from `sourceProductIds` — a grouped
   * row passes `groupMarkets(rows)` (feed label + language when sent); keys of `marketHrefs`.
   */
  marketLabels?: string[] | undefined;
  className?: string | undefined;
}

/**
 * The one product cell every catalog-enrichment product table uses: image + title (takes the free
 * width, wraps to two lines at Normal/Spacious, one line at Compact) + a muted second line
 * with identifiers and market chips. Several feed rows of one product (US + UK) render as one
 * cell with market chips; the long connector path is shortened to its offer id, full value on hover.
 */
export function ProductCell({
  title,
  imageUrl,
  fullImageUrl,
  gallery,
  sourceProductIds,
  meta,
  badges,
  badgesBelow,
  density = 'normal',
  renderTitle,
  marketHrefs,
  marketLabels,
  className,
}: Props) {
  const d = DENSITY[density];
  // S8 — wherever this cell renders (a batch's Products row, the needs-attention queue…), its
  // market chips carry the ambient `from` back to that context, same as every other cross-link.
  const crossLinkSearch = useCrossLinkSearch();
  const parsed = (sourceProductIds ?? [])
    .map((s) => parseSourceProductId(s))
    .filter((p): p is NonNullable<typeof p> => p !== null);
  const offerIds = [...new Set(parsed.map((p) => p.offerId))];
  const markets = marketLabels ?? [
    ...new Set(parsed.map((p) => p.country).filter((c): c is string => !!c)),
  ];
  const language = parsed.find((p) => p.language)?.language ?? null;
  const rawIds = parsed.map((p) => p.raw).join('\n');
  const titleNode = (
    <span className={cn('text-foreground font-medium', d.twoLine ? 'line-clamp-2' : 'truncate')}>
      {title}
    </span>
  );

  return (
    <div className={cn('flex min-w-0 items-center gap-3', className)}>
      <ProductThumb
        src={imageUrl}
        fullSrc={fullImageUrl}
        title={title}
        size={d.thumb}
        gallery={gallery}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-2">
          {renderTitle ? renderTitle(titleNode) : titleNode}
          {badges}
        </div>
        {badgesBelow ? (
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs">{badgesBelow}</div>
        ) : null}
        {(d.twoLine || !!meta) && (offerIds.length > 0 || markets.length > 0 || meta) && (
          <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            {meta}
            {offerIds.length > 0 && (
              <span className="truncate font-mono" title={rawIds}>
                {offerIds.join(', ')}
                {language ? ` · ${language}` : ''}
              </span>
            )}
            {markets.map((m) => {
              const href = marketHrefs?.[m];
              const chip = (
                <Badge
                  variant="outline"
                  className={cn(
                    'h-5 gap-0.5 px-1.5 text-xs font-medium',
                    href?.flagged && 'border-warning bg-warning-50 text-warning',
                  )}
                >
                  {href?.flagged && <TriangleAlert aria-hidden="true" className="size-2.5" />}
                  {m}
                </Badge>
              );
              if (!href) return <span key={m}>{chip}</span>;
              return (
                <Link
                  key={m}
                  to="/catalog-enrichment/feed-products/$feedProductId"
                  params={{ feedProductId: href.feedProductId }}
                  search={crossLinkSearch}
                  className="cursor-pointer hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {chip}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
